import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import './ControlPrimitives.css';

type Props = {
  playbackRate: number;
  setPlaybackRate: (v: number) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>; // nullable ref
  isFullscreen?: boolean;
  controlHeight?: number;
  displayMode?: 'full' | 'value-only';
  /** When provided, replaces the default preset list (e.g. YouTube's supported rates). */
  speedPresets?: number[];
  /** Upload/local videos can use arbitrary positive manual speeds; YouTube stays preset-only. */
  manualEntryEnabled?: boolean;
};

type StepDirection = -1 | 1;

const SPEED_EPSILON = 0.0001;
const UPLOAD_SPEED_MIN = 0.2;
const UPLOAD_SPEED_MAX = 1.2;
const UPLOAD_SPEED_STEP = 0.1;

const areRatesEqual = (a: number, b: number) => Math.abs(a - b) <= SPEED_EPSILON;
const roundSpeed = (value: number, precision = 100) => Number((Math.round(value * precision) / precision).toFixed(4));
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const formatSpeedValue = (value: number): string => {
  const normalized = Number.isFinite(value) ? roundSpeed(value) : 1;
  const text = normalized.toFixed(2).replace(/\.?0+$/, '');
  return `${text}x`;
};

export const parseManualSpeedInput = (rawValue: string): number | null => {
  const trimmed = rawValue.trim().toLowerCase();
  const withoutSuffix = trimmed.endsWith('x')
    ? trimmed.slice(0, -1).trim()
    : trimmed;

  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(withoutSuffix)) {
    return null;
  }

  const parsed = Number(withoutSuffix);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizePresetRates = (presets: number[]) => {
  return Array.from(new Set(
    presets
      .filter(rate => Number.isFinite(rate) && rate > 0)
      .map(rate => roundSpeed(rate))
  )).sort((a, b) => a - b);
};

const getNearestPresetRate = (value: number, presets: number[]) => {
  const current = Number.isFinite(value) ? value : 1;
  return presets.reduce((nearest, preset) => (
    Math.abs(preset - current) < Math.abs(nearest - current) ? preset : nearest
  ), presets[0] ?? 1);
};

export const getUploadStepperRate = (currentRate: number, direction: StepDirection): number => {
  const current = Number.isFinite(currentRate) ? currentRate : 1;

  if (direction < 0) {
    if (current > UPLOAD_SPEED_MAX + SPEED_EPSILON) return UPLOAD_SPEED_MAX;
    if (current <= UPLOAD_SPEED_MIN + SPEED_EPSILON) return current;

    const next = Math.floor((current - SPEED_EPSILON) / UPLOAD_SPEED_STEP) * UPLOAD_SPEED_STEP;
    return roundSpeed(clamp(next, UPLOAD_SPEED_MIN, UPLOAD_SPEED_MAX));
  }

  if (current < UPLOAD_SPEED_MIN - SPEED_EPSILON) return UPLOAD_SPEED_MIN;
  if (current >= UPLOAD_SPEED_MAX - SPEED_EPSILON) return current;

  const next = Math.ceil((current + SPEED_EPSILON) / UPLOAD_SPEED_STEP) * UPLOAD_SPEED_STEP;
  return roundSpeed(clamp(next, UPLOAD_SPEED_MIN, UPLOAD_SPEED_MAX));
};

export const getPresetStepperRate = (
  currentRate: number,
  direction: StepDirection,
  presets: number[]
): number => {
  const supportedPresets = normalizePresetRates(presets);
  const current = Number.isFinite(currentRate) ? currentRate : 1;

  if (supportedPresets.length === 0) return current;

  if (direction < 0) {
    const nextLower = [...supportedPresets].reverse().find(rate => rate < current - SPEED_EPSILON);
    if (nextLower !== undefined) return nextLower;
    return current;
  }

  const nextHigher = supportedPresets.find(rate => rate > current + SPEED_EPSILON);
  if (nextHigher !== undefined) return nextHigher;
  return current;
};

const SpeedSlider: React.FC<Props> = ({
  playbackRate,
  setPlaybackRate,
  videoRef,
  isFullscreen = false,
  controlHeight = 44,
  displayMode = 'full',
  speedPresets,
  manualEntryEnabled,
}) => {
  const compactHeight = Math.max(30, controlHeight);
  const presetOnly = Boolean(speedPresets);
  const canEditManually = manualEntryEnabled ?? !presetOnly;
  const supportedPresets = useMemo(() => normalizePresetRates(speedPresets ?? []), [speedPresets]);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const finalizingManualEditRef = useRef(false);

  const displayRate = presetOnly && supportedPresets.length > 0
    ? getNearestPresetRate(playbackRate, supportedPresets)
    : playbackRate;
  const displayText = formatSpeedValue(displayRate);

  const applyRate = useCallback((rate: number) => {
    if (!Number.isFinite(rate) || rate <= 0) return;

    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  }, [setPlaybackRate, videoRef]);

  useEffect(() => {
    if (!presetOnly || supportedPresets.length === 0) return;

    const nearestSupportedRate = getNearestPresetRate(playbackRate, supportedPresets);
    if (!areRatesEqual(nearestSupportedRate, playbackRate)) {
      applyRate(nearestSupportedRate);
    }
  }, [applyRate, playbackRate, presetOnly, supportedPresets]);

  useEffect(() => {
    if (!isEditing) {
      finalizingManualEditRef.current = false;
      return;
    }

    setDraftValue(formatSpeedValue(playbackRate).replace(/x$/, ''));
  }, [isEditing, playbackRate]);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const getNextRate = useCallback((direction: StepDirection) => {
    if (presetOnly) {
      return getPresetStepperRate(playbackRate, direction, supportedPresets);
    }

    return getUploadStepperRate(playbackRate, direction);
  }, [playbackRate, presetOnly, supportedPresets]);

  const decreaseRate = getNextRate(-1);
  const increaseRate = getNextRate(1);
  const canDecrease = !areRatesEqual(decreaseRate, playbackRate);
  const canIncrease = !areRatesEqual(increaseRate, playbackRate);

  const stepPlaybackRate = useCallback((direction: StepDirection) => {
    const nextRate = getNextRate(direction);
    if (areRatesEqual(nextRate, playbackRate)) return;

    setIsEditing(false);
    applyRate(nextRate);
  }, [applyRate, getNextRate, playbackRate]);

  const startManualEntry = () => {
    if (!canEditManually) return;
    setIsEditing(true);
  };

  const finishManualEntry = (shouldCommit: boolean) => {
    if (finalizingManualEditRef.current) return;
    finalizingManualEditRef.current = true;

    if (shouldCommit) {
      const parsedValue = parseManualSpeedInput(draftValue);
      if (parsedValue !== null) {
        applyRate(parsedValue);
      }
    }

    setIsEditing(false);
  };

  const handleChipKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isEditing) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      stepPlaybackRate(-1);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      stepPlaybackRate(1);
    }
  };

  return (
    <div
      className={`preset-chip-button preset-chip-button-speed speed-split-chip ${
        displayMode === 'value-only'
          ? 'preset-chip-button-value-only'
          : 'preset-chip-button-labeled'
      } ${isFullscreen ? 'speed-split-chip-fullscreen' : ''}`}
      style={{ minHeight: `${compactHeight}px` }}
      role="group"
      aria-label="Playback speed"
      onKeyDown={handleChipKeyDown}
    >
      <button
        type="button"
        className="speed-split-zone speed-split-edge speed-split-decrease"
        onClick={() => stepPlaybackRate(-1)}
        disabled={!canDecrease}
        title={canDecrease ? 'Decrease playback speed' : 'Minimum speed reached'}
        aria-label="Decrease playback speed"
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>

      {isEditing ? (
        <input
          ref={inputRef}
          className="speed-split-input"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={() => finishManualEntry(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              finishManualEntry(true);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              finishManualEntry(false);
            }
          }}
          aria-label="Playback speed manual entry"
          inputMode="decimal"
        />
      ) : (
        <button
          type="button"
          className="speed-split-zone speed-split-value-zone"
          onClick={startManualEntry}
          title={canEditManually ? 'Edit playback speed' : 'YouTube supports preset speeds only'}
          aria-label={
            canEditManually
              ? `Edit playback speed, current speed ${displayText}`
              : `Current playback speed ${displayText}. YouTube supports preset speeds only`
          }
        >
          <span className="preset-chip-value">{displayText}</span>
          {canEditManually && <Pencil className="speed-split-edit-hint" size={11} aria-hidden="true" />}
        </button>
      )}

      <button
        type="button"
        className="speed-split-zone speed-split-edge speed-split-increase"
        onClick={() => stepPlaybackRate(1)}
        disabled={!canIncrease}
        title={canIncrease ? 'Increase playback speed' : 'Maximum speed reached'}
        aria-label="Increase playback speed"
      >
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  );
};

export default SpeedSlider;
